import React, { useContext, useEffect, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData from "../Datas/productData";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiCheck, FiLock, FiMinus, FiPlus } from "react-icons/fi";
import Header from "./Header";
import siteConfig, { formatPrice } from "../config/siteConfig";

const initialForm = {
  email: "",
  country: siteConfig.operatingRegion,
  firstName: "",
  lastName: "",
  address: "",
  apartment: "",
  city: "",
  state: "",
  postalCode: "",
};

const CheckOut = () => {
  const { cartItems, updateQuantity } = useContext(CartContext);
  const [step, setStep] = useState("address");
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [placingOrder, setPlacingOrder] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]');
    const previous = robots?.getAttribute("content") || "index, follow";
    robots?.setAttribute("content", "noindex, nofollow");
    document.title = `Secure checkout | ${siteConfig.brandName}`;
    return () => robots?.setAttribute("content", previous);
  }, []);

  const items = useMemo(
    () => cartItems.map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) })).filter((item) => item.product),
    [cartItems]
  );

  const subtotal = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = siteConfig.shippingFee;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validateAddress = () => {
    const next = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email address.";
    ["firstName", "lastName", "address", "city", "state", "postalCode"].forEach((field) => {
      if (!form[field].trim()) next[field] = "This field is required.";
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const continueFromAddress = (event) => {
    event.preventDefault();
    if (validateAddress()) { setStep("delivery"); window.scrollTo(0, 0); }
  };

  const continueFromDelivery = (event) => {
    event.preventDefault();
    setStep("payment");
    window.scrollTo(0, 0);
  };

  const placeOrder = (event) => {
    event.preventDefault();
    setPlacingOrder(true);
    const orderNo = `${Date.now()}${Math.floor(100000 + Math.random() * 900000)}`;
    const origin = window.location.origin;
    const params = new URLSearchParams({
      orderNo,
      amount: total.toFixed(2),
      currency: siteConfig.currency,
      email: form.email,
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: items[0].product.name,
    });
    window.sessionStorage.setItem(siteConfig.storage.pendingOrder, JSON.stringify({ orderNo, email: form.email, total }));
    window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
  };

  if (!items.length) {
    return (
      <div className="checkout-page">
        <Header />
        <main className="checkout-empty">
          <h1>Your bag is empty.</h1>
          <p>Add something before heading to checkout.</p>
          <Link className="app-button" to="/shop">Return to shop</Link>
        </main>
      </div>
    );
  }

  const addressSummary = `${form.firstName} ${form.lastName}, ${form.address}, ${form.city}, ${form.state} ${form.postalCode}`;

  return (
    <div className="checkout-page">
      <Header />
      <main className="checkout">
        <section className="checkout__form-column">
          <div className="checkout__form-wrap">
            <nav className="checkout__steps" aria-label="Checkout progress">
              {["address", "delivery", "payment"].map((item, index) => (
                <button key={item} className={step === item ? "is-active" : ""} disabled={index > ["address", "delivery", "payment"].indexOf(step)} onClick={() => setStep(item)}>
                  <span>{index + 1}</span>{item}
                </button>
              ))}
            </nav>

            <button className="checkout__summary-toggle" onClick={() => setSummaryOpen((open) => !open)}>
              <span>Order summary · {items.length} {items.length === 1 ? "item" : "items"}</span><strong>{formatPrice(total)}</strong>
            </button>
            {summaryOpen && <MobileSummary items={items} subtotal={subtotal} shipping={shipping} total={total} />}

            {step === "address" && (
              <form onSubmit={continueFromAddress} noValidate>
                <header className="checkout__section-heading"><span className="eyebrow">Step 1 of 3</span><h1>Where should we send it?</h1></header>
                <fieldset className="checkout__fields">
                  <legend>Contact</legend>
                  <Field label="Email" name="email" type="email" value={form.email} error={errors.email} onChange={updateField} full />
                </fieldset>
                <fieldset className="checkout__fields">
                  <legend>Delivery address</legend>
                  <label className="checkout-field checkout-field--full"><span>Country / region</span><select name="country" value={form.country} onChange={updateField}>{siteConfig.shippingCountries.map((country) => <option key={country}>{country}</option>)}</select></label>
                  <Field label="First name" name="firstName" value={form.firstName} error={errors.firstName} onChange={updateField} />
                  <Field label="Last name" name="lastName" value={form.lastName} error={errors.lastName} onChange={updateField} />
                  <Field label="Address" name="address" value={form.address} error={errors.address} onChange={updateField} full />
                  <Field label="Apartment, suite, etc. (optional)" name="apartment" value={form.apartment} onChange={updateField} full />
                  <Field label="City" name="city" value={form.city} error={errors.city} onChange={updateField} />
                  <Field label="State" name="state" value={form.state} error={errors.state} onChange={updateField} />
                  <Field label="ZIP / postal code" name="postalCode" value={form.postalCode} error={errors.postalCode} onChange={updateField} />
                </fieldset>
                <div className="checkout__continue"><Link to="/cartpage"><FiArrowLeft /> Return to bag</Link><button className="app-button" type="submit">Continue to delivery <FiArrowRight /></button></div>
              </form>
            )}

            {step === "delivery" && (
              <form onSubmit={continueFromDelivery}>
                <header className="checkout__section-heading"><span className="eyebrow">Step 2 of 3</span><h1>Choose delivery.</h1></header>
                <div className="checkout__address-card"><span>Ship to</span><p>{addressSummary}</p><button type="button" onClick={() => setStep("address")}>Edit</button></div>
                <fieldset className="checkout__shipping-options">
                  <legend>Delivery method</legend>
                  <label><input type="radio" name="shipping" checked readOnly /><span><strong>Standard tracked</strong><small>3–6 business days</small></span><b>{formatPrice(shipping)}</b></label>
                </fieldset>
                <div className="checkout__continue"><button type="button" className="checkout__back" onClick={() => setStep("address")}><FiArrowLeft /> Back</button><button className="app-button" type="submit">Continue to payment <FiArrowRight /></button></div>
              </form>
            )}

            {step === "payment" && (
              <form onSubmit={placeOrder} noValidate>
                <header className="checkout__section-heading"><span className="eyebrow">Step 3 of 3</span><h1>Secure payment.</h1><p><FiLock /> Continue to our secure payment provider. No card details are entered or stored on this website.</p></header>
                <div className="checkout__address-card"><span>Delivery</span><p>{addressSummary} · Standard tracked</p><button type="button" onClick={() => setStep("delivery")}>Edit</button></div>
                <fieldset className="checkout__fields checkout__payment-box">
                  <legend>Payment <span>Available methods appear on the payment page</span></legend>
                  <p className="checkout__provider-note">After clicking Pay now, you will be redirected to the payment page to choose an available payment method and complete the order.</p>
                </fieldset>
                <p className="checkout__terms">By placing the order, you agree to our <Link to="/terms-of-sale">terms of sale</Link> and <Link to="/orders-payment">payment policy</Link>, and acknowledge our <Link to="/privacy">privacy</Link> and <Link to="/returns">returns</Link> policies.</p>
                <div className="checkout__continue"><button type="button" className="checkout__back" onClick={() => setStep("delivery")}><FiArrowLeft /> Back</button><button className="app-button" type="submit" disabled={placingOrder}>{placingOrder ? "Redirecting…" : `Pay now · ${formatPrice(total)}`} {!placingOrder && <FiArrowRight />}</button></div>
              </form>
            )}
          </div>
        </section>

        <aside className="checkout__summary-column">
          <div className="checkout-summary">
            <h2>Order summary</h2>
            <div className="checkout-summary__items">
              {items.map((item) => (
                <article key={`${item.id}-${item.size}-${item.color}`}>
                  <div className="checkout-summary__image"><img src={item.product.image} alt="" /><span>{item.quantity}</span></div>
                  <div><h3>{item.product.name}</h3><p>{item.color} · {item.size}</p><div className="checkout-summary__qty"><button onClick={() => updateQuantity(item.id, item.size, item.color, Math.max(1, item.quantity - 1))} aria-label="Decrease"><FiMinus /></button><span>{item.quantity}</span><button onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)} aria-label="Increase"><FiPlus /></button></div></div>
                  <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                </article>
              ))}
            </div>
            <div className="checkout-summary__totals"><p><span>Subtotal</span><strong>{formatPrice(subtotal)}</strong></p><p><span>Shipping</span><strong>{shipping ? formatPrice(shipping) : "Free"}</strong></p><p><span>Total <small>{siteConfig.currency}</small></span><strong>{formatPrice(total)}</strong></p></div>
            <div className="checkout-summary__secure"><FiCheck /><span>30-day returns</span><FiLock /><span>Secure checkout</span></div>
          </div>
        </aside>
      </main>
    </div>
  );
};

function Field({ label, name, value, error, onChange, full = false, type = "text", ...inputProps }) {
  return (
    <label className={`checkout-field ${full ? "checkout-field--full" : ""} ${error ? "has-error" : ""}`}>
      <span>{label}</span>
      <input type={type} name={name} value={value} onChange={onChange} placeholder={label} {...inputProps} />
      {error && <small>{error}</small>}
    </label>
  );
}

function MobileSummary({ items, subtotal, shipping, total }) {
  return (
    <div className="checkout__mobile-summary">
      {items.map((item) => <p key={`${item.id}-${item.size}`}><span>{item.quantity} × {item.product.name}</span><strong>{formatPrice(item.product.price * item.quantity)}</strong></p>)}
      <p><span>Subtotal</span><strong>{formatPrice(subtotal)}</strong></p>
      <p><span>Shipping</span><strong>{shipping ? formatPrice(shipping) : "Free"}</strong></p>
      <p><span>Total</span><strong>{formatPrice(total)}</strong></p>
    </div>
  );
}

export default CheckOut;
